// @ts-nocheck
/* ============================================================
   Weather rules per service (plan §5.6, roadmap §13 item 7).

   A fence day is about mud and frost: rain stops the pour, a hard freeze
   stops the auger. Concrete has three different days in it:
     • a BOND day (coating, sealing, caulking) needs a dry slab, air and slab
       above 40°F, and the dew point well below the day's high — moisture
       condensing on the slab is what makes a coating peel;
     • a LIFT day is forgiving: foam cures under the slab in most weather, but a
       downpour floods the holes and frozen ground moves again when it thaws;
     • a CLEANING day only needs the water not to freeze.
   The thresholds are defaults; the Weather automation's concrete settings
   (config.byVertical.concrete: threshold, minTemp, dewSpread) override them.

   `forecast` is open-meteo's day: { rainProb %, lo °F, hi °F, dew °F } — any
   field may be missing. Import-free; mirrored by server/src/weatherrules.js
   (unit-weatherrules.mjs pins the two equal). The fence grade is the one the
   work order banner has always shown, untouched.
   ============================================================ */

export const BOND_TYPES = ["coat", "seal", "caulk"];
export const WEATHER_DEFAULTS = {
  fence: { rain: 60 },
  concrete: {
    bond: { rain: 30, minTemp: 40, dewSpread: 5 },
    lift: { rain: 70, minTemp: 20 },
    clean: { rain: 80, minTemp: 32 },
  },
};

const num = (v: any): number | null => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));
const types = (jobTypes: any): string[] => (Array.isArray(jobTypes) ? jobTypes.filter((j: any) => typeof j === "string") : []);

/* what the note calls the work: the most weather-sensitive job type wins */
export function workLabel(jobTypes: any): string {
  const t = types(jobTypes);
  if (t.includes("coat")) return "the floor coating";
  if (t.includes("seal")) return "sealing";
  if (t.includes("caulk")) return "caulking";
  if (t.includes("lift")) return "lifting";
  if (t.includes("clean")) return "cleaning";
  return "your concrete work";
}

/* Should this day's job be flagged? Returns the reasons in plain words so the
   crew flag, the reschedule note and the schedule banner all say the same thing. */
export function weatherHold(forecast: any, vertical: any, jobTypes: any, cfg: any) {
  const f = forecast && typeof forecast === "object" ? forecast : {};
  const c = cfg && typeof cfg === "object" ? cfg : {};
  const rain = num(f.rainProb) ?? num(f.rain) ?? 0;
  const lo = num(f.lo), hi = num(f.hi), dew = num(f.dew);
  const reasons: string[] = [];
  if (vertical !== "concrete") {
    const th = num(c.threshold) ?? WEATHER_DEFAULTS.fence.rain;
    if (rain >= th) reasons.push(`${rain}% chance of rain`);
    return { hold: reasons.length > 0, reasons, why: reasons.join("; "), work: "your install", level: reasons.length ? "high" : "ok" };
  }
  const t = types(jobTypes);
  // no job types known → the strict rules: better a false flag than a peeled floor
  const bond = !t.length || t.some((j) => BOND_TYPES.includes(j));
  const lift = t.includes("lift");
  const clean = t.includes("clean");
  const D = WEATHER_DEFAULTS.concrete;
  let level = "ok";
  if (bond) {
    const th = num(c.threshold) ?? D.bond.rain, minT = num(c.minTemp) ?? D.bond.minTemp, spread = num(c.dewSpread) ?? D.bond.dewSpread;
    if (rain >= th) reasons.push(`${rain}% chance of rain — the slab has to be dry`);
    if (lo != null && lo <= minT) reasons.push(`${Math.round(lo)}°F low — coatings, sealers and caulk need ${minT}°F+`);
    if (dew != null && hi != null && hi - dew <= spread) reasons.push(`dew point within ${Math.round(hi - dew)}°F of the high — moisture on the slab`);
    if (!reasons.length && (rain >= th / 2 || (lo != null && lo <= minT + 8))) level = "med";
  }
  if (lift) {
    const th = num(c.threshold) ?? D.lift.rain, minT = num(c.liftMinTemp) ?? D.lift.minTemp;
    if (rain >= Math.max(th, D.lift.rain)) reasons.push(`${rain}% chance of rain — a downpour floods the holes`);
    if (lo != null && lo <= minT) reasons.push(`${Math.round(lo)}°F low — frozen ground moves again when it thaws`);
    if (!reasons.length && level === "ok" && (rain >= D.lift.rain - 20 || (lo != null && lo <= D.lift.minTemp + 8))) level = "med";
  }
  if (clean && !bond) {
    if (rain >= D.clean.rain) reasons.push(`${rain}% chance of rain`);
    if (lo != null && lo <= D.clean.minTemp) reasons.push(`${Math.round(lo)}°F low — wash water freezes`);
  }
  const hold = reasons.length > 0;
  return { hold, reasons, why: reasons.join("; "), work: workLabel(t), level: hold ? "high" : level };
}

/* the schedule banner's grade for one forecast day. A fence day is graded the
   way it always was (lib/weather.ts risk); a concrete day by the rules above. */
export function weatherRisk(day: any, opts: any) {
  if (!day) return null;
  const o = opts && typeof opts === "object" ? opts : {};
  if (o.vertical !== "concrete") {
    if (day.rain >= 70) return { level: "high", label: day.rain + "% rain", why: "Heavy rain risk — concrete pours and augers don't mix with mud." };
    if (isFinite(day.lo) && day.lo <= 15) return { level: "high", label: Math.round(day.lo) + "°F low", why: "Hard freeze — frost depth slows digging and concrete cure." };
    if (day.rain >= 40) return { level: "med", label: day.rain + "% rain", why: "Rain possible — have a tarp plan for the pour." };
    if (isFinite(day.lo) && day.lo <= 28) return { level: "med", label: Math.round(day.lo) + "°F low", why: "Frost likely overnight — plan a later start." };
    return { level: "ok", label: (day.rain || 0) + "% rain" };
  }
  const h = weatherHold({ rainProb: day.rain, lo: day.lo, hi: day.hi, dew: day.dew }, "concrete", o.jobTypes, o.cfg);
  const label = h.reasons.length ? h.reasons[0].split(" — ")[0] : (day.rain || 0) + "% rain";
  if (h.hold) return { level: "high", label, why: `Not a day for ${h.work}: ${h.why}.` };
  if (h.level === "med") return { level: "med", label: isFinite(day.lo) && day.lo <= 48 ? Math.round(day.lo) + "°F low" : (day.rain || 0) + "% rain", why: `Borderline for ${h.work} — check the slab temperature and the dew point in the morning.` };
  return { level: "ok", label };
}
